/**
 * ローカル文字列類似度スコアリング（APIキーなし時のフォールバック）
 * ひらがな・カタカナ・漢字の表記ゆれを正規化して比較
 */
import { convertForDisplay } from './kanjiConverter';

// テキストを正規化（スペース・句読点・記号を除去、カタカナ→ひらがな）
function normalize(text) {
  return text
    .replace(/[\s　]/g, '')           // スペース除去
    .replace(/[、。！？「」『』・…]/g, '') // 句読点・記号除去
    .replace(/[\u30A1-\u30F6]/g, (c) =>
      String.fromCharCode(c.charCodeAt(0) - 0x60) // カタカナ→ひらがな
    )
    .toLowerCase();
}

// 文字単位のマッチ率（0〜1）
function charMatchRate(a, b) {
  if (!a || !b) return 0;
  const na = normalize(a);
  const nb = normalize(b);
  if (na.length === 0 && nb.length === 0) return 1;
  if (na.length === 0 || nb.length === 0) return 0;

  // 共通文字数をカウント（順序考慮なし・簡易版）
  const shorter = na.length <= nb.length ? na : nb;
  const longer  = na.length <= nb.length ? nb : na;
  let matched = 0;
  const used = new Array(longer.length).fill(false);
  for (const ch of shorter) {
    const idx = longer.split('').findIndex((c, i) => !used[i] && c === ch);
    if (idx !== -1) { matched++; used[idx] = true; }
  }
  return matched / longer.length;
}

// LCS（最長共通部分列）ベースの類似度
function lcsRate(a, b) {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return 0;

  const m = na.length, n = nb.length;
  // 長すぎる場合は簡易版に切り替え
  if (m * n > 10000) return charMatchRate(a, b);

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = na[i - 1] === nb[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const lcs = dp[m][n];

  // 【採点方式】お手本の何割を読めたか（recall のみ）
  // precision を混ぜると「1文だけ完璧に読んだ」場合に不当に高点数になるため除外
  // Whisperの余分な言葉も影響しない
  const recall = lcs / m;
  return recall;
}

/**
 * ローカル採点（APIキーなし時フォールバック）
 * @param {string} original お手本テキスト
 * @param {string} transcript 文字起こし結果
 * @returns {{ score: number, stars: number, feedback: string, display: string }}
 */
// 点数をきりのいい数字に丸める
// 80未満 → 10点刻み、80以上 → 5点刻み、95以上 → 100点
function snapScore(raw) {
  if (raw >= 95) return 100;
  if (raw >= 80) return Math.round(raw / 5) * 5;
  return Math.round(raw / 10) * 10;
}

export function localScore(original, transcript) {
  const rate = lcsRate(original, transcript);
  // 平方根カーブで中間点を引き上げ → きりのいい点数に丸める
  const score = snapScore(Math.min(100, Math.round(Math.sqrt(rate) * 100)));

  let stars, feedback;
  if (score >= 70) {
    stars = 3;
    feedback = 'かんぺき！　すごくじょうず！';
  } else if (score >= 45) {
    stars = 2;
    feedback = 'よくよめたね！';
  } else if (score >= 20) {
    stars = 1;
    feedback = 'がんばったね！　ゆっくりよんでみよう！';
  } else {
    stars = 1;
    feedback = 'もういちどちゃれんじ！　おてほんをよくみてね！';
  }

  // display は transcript から生成（学年相応の漢字変換を適用）
  const display = convertForDisplay(transcript);

  return { score, stars, feedback, display };
}
